import { observer } from "mobx-react";
import React from "react";
import { logger } from "@fidget/common/utils";
import { AgentGroupsSettings } from "../../components/settings/agentGroups/agentGroupsSettings";
import { AssignmentRulesSettings } from "../../components/settings/assignmentRules/assignmentRulesSettings";
import { BillingSettings } from "../../components/settings/billing/billingSettings";
import { BotsSettings } from "../../components/settings/bots/botsSettings";
import { EventActionsSettingsProvider } from "../../components/settings/bots/useEventActionsSettings";
import { GoogleSyncSettings } from "../../components/settings/googleSync/googleSyncSettings";
import { MessagesSettings } from "../../components/settings/messages/messagesSettings";
import { ProactiveMessagesSettings } from "../../components/settings/proactiveMessages/proactiveMessagesSettings";
import { ReportsSettings } from "../../components/settings/reports/reportsSettings";
import { TaggingSettings } from "../../components/settings/tags/taggingSettings/taggingSettings";
import { TagGroupsSettings } from "../../components/settings/tags/tagGroupsSettings/tagGroupsSettings";
import { TagsSettings } from "../../components/settings/tags/tagsSettings/tagsSettings";
import { UsersSettings } from "../../components/settings/users/usersSettings";
import { WebhooksSettings } from "../../components/settings/webhooks/webhooksSettings";
import { WidgetConfigSettings } from "../../components/settings/widgetConfig/widgetConfigSettings";
import { WorkingHoursSettings } from "../../components/settings/workingHours/workingHoursSettings";
import { useStore } from "../../models";
import { DashboardView } from "../dashboard/views/DashboardView";
import styles from "./settingsEditCommon.css";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { RolesSettings } from "../../components/settings/roles/rolesSettings";
import { AgentStatusesSettings } from "../../components/settings/agentStatuses/agentStatusesSettings";
import { AgentAvatarSettings } from "../../components/settings/agentAvatarSettings/agentAvatarSettings";
import { MyProfileSettings } from "../../components/settings/profile/myProfileSettings/myProfileSettings";
import { ManualAssignmentSettings } from "../../components/settings/manualAssignment/ManualAssignmentSettings";
import { useAgentServices } from "../../models/agentServices";

export interface Props {
  view: DashboardView;
}

export const SettingsEdit = observer(({ view }: Props) => {
  const { currentUserStore } = useStore();
  const { spinnerService } = useAgentServices();

  const renderContent = () => {
    switch (view) {
      case DashboardView.SETTINGS_PROFILE:
        return <MyProfileSettings />;

      case DashboardView.SETTINGS_SETTINGS:
        return <GoogleSyncSettings />;

      case DashboardView.SETTINGS_USERS:
        return <UsersSettings />;

      case DashboardView.SETTINGS_ADVISER_GROUPS:
        return <AgentGroupsSettings />;

      case DashboardView.SETTINGS_ADVISER_STATUSES:
        return <AgentStatusesSettings />;

      case DashboardView.SETTINGS_ADVISER_AVATAR:
        return <AgentAvatarSettings />;

      case DashboardView.SETTINGS_ASSIGNMENT_RULES:
        return <AssignmentRulesSettings orgConfig={currentUserStore.orgConfig!} />;

      case DashboardView.SETTINGS_MANUAL_ASSIGNMENT:
        return <ManualAssignmentSettings />;

      case DashboardView.SETTINGS_BILLING:
        return <BillingSettings />;

      case DashboardView.SETTINGS_BOTS:
        return (
          <EventActionsSettingsProvider>
            <BotsSettings />
          </EventActionsSettingsProvider>
        );

      case DashboardView.SETTINGS_PROACTIVE_MESSAGES:
        return <ProactiveMessagesSettings />;

      case DashboardView.SETTINGS_TAGGING:
        return <TaggingSettings />;

      case DashboardView.SETTINGS_TAGS:
        return <TagsSettings />;

      case DashboardView.SETTINGS_TAG_GROUPS:
        return <TagGroupsSettings />;

      case DashboardView.SETTINGS_WIDGET_CONFIG:
        return (
          <WidgetConfigSettings
            orgConfig={currentUserStore.orgConfig!}
            spinnerService={spinnerService}
          />
        );

      case DashboardView.SETTINGS_MESSAGES:
        return <MessagesSettings />;

      case DashboardView.SETTINGS_WORKING_HOURS:
        return <WorkingHoursSettings workingHours={currentUserStore.workingHours!} zoneId={currentUserStore.zoneId!} />;

      case DashboardView.SETTINGS_REPORTS:
        return <ReportsSettings orgConfig={currentUserStore.orgConfig!} />;

      case DashboardView.SETTINGS_WEBHOOKS:
        return <WebhooksSettings />;

      case DashboardView.SETTINGS_ROLES:
        return <RolesSettings />;

      // case handled in different component, added to be able recognize unhandled cases in default case
      case DashboardView.CONVERSATIONS:
      case DashboardView.CALENDAR:
      case DashboardView.AI_SETTINGS_GENERAL:
      case DashboardView.AI_SETTINGS_MODEL_CONFIGURATION:
      case DashboardView.AI_SETTINGS_TESTING:
      case DashboardView.REPORTS_EFFICIENCY:
      case DashboardView.REPORTS_SERVICE_LEVEL_TAG_GROUPS:
      case DashboardView.REPORTS_SERVICE_LEVEL_TAGS:
      case DashboardView.REPORTS_BACK_LINE_TAGS:
      case DashboardView.REPORTS_ADVISER_GROUPS:
      case DashboardView.REPORTS_ADVISER_TAG_GROUPS:
      case DashboardView.REPORTS_OTHER:
        return;
      default:
        assertUnreachable(view);
        logger.info("Unknown DashboardView");

        return;
    }
  };

  const render = () => (
    <div className={styles["container-scroll"]}>
      <div className={styles.container}>{renderContent()}</div>
    </div>
  );

  return render();
});
